import { Children, Fragment, cloneElement, isValidElement, type ReactNode } from "react";

const SERIF_MARKS = new Set(["serif", "darkGreenSerif", "lightGreenSerif", "lightGreenHighlight", "darkGreenHighlight"]);

const SPACE = /([ \t\r\n]+)/;
const LEAD = /^[ \t\r\n]+/;
const TRAIL = /[ \t\r\n]+$/;

function words(text: string, serif: boolean, key: string): ReactNode[] {
  return text.split(SPACE).map((part, i) => {
    if (!part) return null;
    if (SPACE.test(part)) return part;
    return (
      <span
        key={`${key}-${i}`}
        className="rv-mask">
        <span className={serif ? "rv-unit rv-serif" : "rv-unit"}>{part}</span>
      </span>
    );
  });
}

function edges(node: ReactNode): [string, string] {
  const all = Children.toArray(node);
  const head = all[0];
  const tail = all[all.length - 1];
  return [
    typeof head === "string" ? (head.match(LEAD)?.[0] ?? "") : "",
    typeof tail === "string" ? (tail.match(TRAIL)?.[0] ?? "") : "",
  ];
}

function trim(node: ReactNode, [lead, trail]: [string, string]): ReactNode {
  if (!lead && !trail) return node;
  const all = Children.toArray(node);
  if (typeof all[0] === "string" && lead) all[0] = all[0].slice(lead.length);
  const last = all.length - 1;
  if (typeof all[last] === "string" && trail) all[last] = (all[last] as string).slice(0, -trail.length);
  return all;
}

export function splitWords(node: ReactNode, serif = false, key = "w"): ReactNode {
  if (typeof node === "string") return words(node, serif, key);
  if (Array.isArray(node)) return node.map((child, i) => splitWords(child, serif, `${key}${i}`));
  if (isValidElement<{ children?: ReactNode; markType?: string }>(node)) {
    const markType = node.props.markType;
    const inner = node.props.children;
    const [lead, trail] = edges(inner);
    const split = splitWords(trim(inner, [lead, trail]), serif || (!!markType && SERIF_MARKS.has(markType)), `${key}m`);
    return (
      <Fragment key={node.key ?? key}>
        {lead}
        {cloneElement(node, undefined, split)}
        {trail}
      </Fragment>
    );
  }
  return node;
}
